import type { Point, Rect } from './types';

/**
 * Warp is a bicubic Bezier patch: a 4x4 net of control points in document
 * pixels, stored row by row from the top. The four corners sit on the shape, the
 * twelve others are the direction handles, which is exactly the grid Photoshop
 * draws over a warped layer.
 */
export type WarpMesh = readonly Point[];

export const WARP_ORDER = 4;
export const WARP_POINTS = WARP_ORDER * WARP_ORDER;

export const WARP_PRESETS = [
  'custom',
  'arc',
  'arcLower',
  'arcUpper',
  'arch',
  'bulge',
  'shellLower',
  'shellUpper',
  'flag',
  'wave',
  'fish',
  'rise',
  'fisheye',
  'inflate',
  'squeeze',
  'twist',
] as const;

export type WarpPreset = (typeof WARP_PRESETS)[number];

export const WARP_PRESET_LABELS: Record<WarpPreset, string> = {
  custom: 'Custom',
  arc: 'Arc',
  arcLower: 'Arc Lower',
  arcUpper: 'Arc Upper',
  arch: 'Arch',
  bulge: 'Bulge',
  shellLower: 'Shell Lower',
  shellUpper: 'Shell Upper',
  flag: 'Flag',
  wave: 'Wave',
  fish: 'Fish',
  rise: 'Rise',
  fisheye: 'Fisheye',
  inflate: 'Inflate',
  squeeze: 'Squeeze',
  twist: 'Twist',
};

export function warpIndex(row: number, column: number): number {
  return row * WARP_ORDER + column;
}

/** The net that leaves the box untouched: an evenly spaced grid over `rect`. */
export function identityMesh(rect: Rect): Point[] {
  const mesh: Point[] = [];
  for (let row = 0; row < WARP_ORDER; row += 1) {
    for (let column = 0; column < WARP_ORDER; column += 1) {
      mesh.push({
        x: rect.x + (rect.width * column) / 3,
        y: rect.y + (rect.height * row) / 3,
      });
    }
  }
  return mesh;
}

export function isIdentityMesh(mesh: WarpMesh, rect: Rect): boolean {
  const identity = identityMesh(rect);
  return mesh.every(
    (point, index) =>
      Math.abs(point.x - identity[index].x) < 0.01 &&
      Math.abs(point.y - identity[index].y) < 0.01,
  );
}

function bernstein(t: number): [number, number, number, number] {
  const u = 1 - t;
  return [u * u * u, 3 * u * u * t, 3 * u * t * t, t * t * t];
}

/** Point of the patch at parameters `s` across and `t` down, both 0..1. */
export function warpPoint(mesh: WarpMesh, s: number, t: number): Point {
  const bs = bernstein(s);
  const bt = bernstein(t);
  let x = 0;
  let y = 0;
  for (let row = 0; row < WARP_ORDER; row += 1) {
    for (let column = 0; column < WARP_ORDER; column += 1) {
      const weight = bt[row] * bs[column];
      const point = mesh[warpIndex(row, column)];
      x += weight * point.x;
      y += weight * point.y;
    }
  }
  return { x, y };
}

/**
 * Control points of the cubic that passes through four samples taken at
 * t = 0, 1/3, 2/3, 1. Inverting the Bernstein matrix at those parameters gives
 * these fixed weights.
 */
function interpolateCubic(q: [Point, Point, Point, Point]): [Point, Point, Point, Point] {
  const mix = (a: number, b: number, c: number, d: number): Point => ({
    x: (a * q[0].x + b * q[1].x + c * q[2].x + d * q[3].x) / 6,
    y: (a * q[0].y + b * q[1].y + c * q[2].y + d * q[3].y) / 6,
  });
  return [q[0], mix(-5, 18, -9, 2), mix(2, -9, 18, -5), q[3]];
}

/**
 * Fits a control net through a mapping of the unit square, which is how the
 * presets below are written. Sampling on the 4x4 grid and solving each row and
 * then each column reproduces the mapping exactly at the sample points, and
 * exactly everywhere when the mapping is itself of cubic degree or lower.
 */
export function fitMesh(sample: (s: number, t: number) => Point): Point[] {
  const grid: Point[][] = [];
  for (let row = 0; row < WARP_ORDER; row += 1) {
    const samples: Point[] = [];
    for (let column = 0; column < WARP_ORDER; column += 1) {
      samples.push(sample(column / 3, row / 3));
    }
    grid.push(interpolateCubic(samples as [Point, Point, Point, Point]));
  }

  const mesh: Point[] = new Array(WARP_POINTS);
  for (let column = 0; column < WARP_ORDER; column += 1) {
    const fitted = interpolateCubic([
      grid[0][column],
      grid[1][column],
      grid[2][column],
      grid[3][column],
    ]);
    for (let row = 0; row < WARP_ORDER; row += 1) mesh[warpIndex(row, column)] = fitted[row];
  }
  return mesh;
}

/**
 * The net that reproduces a plain quad, so that a free transform carries over
 * when warping starts. A bilinear map is of degree one, so the fit is exact.
 */
export function meshFromQuad(corners: readonly [Point, Point, Point, Point]): Point[] {
  const [nw, ne, se, sw] = corners;
  return fitMesh((s, t) => ({
    x: (nw.x * (1 - s) + ne.x * s) * (1 - t) + (sw.x * (1 - s) + se.x * s) * t,
    y: (nw.y * (1 - s) + ne.y * s) * (1 - t) + (sw.y * (1 - s) + se.y * s) * t,
  }));
}

/**
 * Each preset as a deformation of the unit square, driven by one bend amount
 * where 0 leaves the square alone. The shapes follow Photoshop's, which are
 * quadratic bends of the top edge, the bottom edge or both.
 */
const DEFORMATIONS: Record<WarpPreset, (s: number, t: number, bend: number) => Point> = {
  custom: (s, t) => ({ x: s, y: t }),
  // A quadratic arc lifts the middle of both edges by the same amount.
  arc: (s, t, bend) => ({ x: s, y: t - bend * arch(s) }),
  arcUpper: (s, t, bend) => ({ x: s, y: t - bend * arch(s) * (1 - t) }),
  arcLower: (s, t, bend) => ({ x: s, y: t - bend * arch(s) * t }),
  // Arch bends the top edge one way and the bottom edge the other.
  arch: (s, t, bend) => ({ x: s, y: t - bend * arch(s) * (1 - 2 * t) }),
  bulge: (s, t, bend) => ({ x: s, y: t + bend * arch(s) * (2 * t - 1) }),
  shellLower: (s, t, bend) => ({ x: s, y: t - bend * s * s * t }),
  shellUpper: (s, t, bend) => ({ x: s, y: t - bend * s * s * (1 - t) }),
  flag: (s, t, bend) => ({ x: s, y: t + bend * Math.sin(s * Math.PI * 2) * 0.5 }),
  wave: (s, t, bend) => ({
    x: s,
    y: t + bend * Math.sin(s * Math.PI * 2) * 0.5 * (1 - 2 * t) * -1,
  }),
  fish: (s, t, bend) => ({ x: s, y: 0.5 + (t - 0.5) * (1 + bend * (1 - s)) }),
  rise: (s, t, bend) => ({ x: s, y: t - bend * s }),
  // Fisheye and Inflate push outwards from the centre.
  fisheye: (s, t, bend) => ({
    x: 0.5 + (s - 0.5) * (1 + bend * arch(t)),
    y: 0.5 + (t - 0.5) * (1 + bend * arch(s)),
  }),
  inflate: (s, t, bend) => ({ x: s, y: 0.5 + (t - 0.5) * (1 + bend * arch(s)) }),
  squeeze: (s, t, bend) => ({ x: s, y: 0.5 + (t - 0.5) * (1 - bend * arch(s)) }),
  twist: (s, t, bend) => {
    const angle = bend * Math.PI * (t - 0.5);
    const dx = s - 0.5;
    const dy = t - 0.5;
    return {
      x: 0.5 + dx * Math.cos(angle) - dy * Math.sin(angle),
      y: 0.5 + dx * Math.sin(angle) + dy * Math.cos(angle),
    };
  },
};

/** Quadratic hump: 0 at the edges, 1 in the middle. */
function arch(s: number): number {
  return 4 * s * (1 - s);
}

/** Control net of a preset, with `bend` in -1..1 as the options bar reports it. */
export function presetMesh(preset: WarpPreset, rect: Rect, bend: number): Point[] {
  if (preset === 'custom') return identityMesh(rect);
  const deform = DEFORMATIONS[preset];
  return fitMesh((s, t) => {
    const local = deform(s, t, bend);
    return { x: rect.x + local.x * rect.width, y: rect.y + local.y * rect.height };
  });
}

/** Box around the warped shape, which is what the transform overlay hugs. */
export function warpBounds(mesh: WarpMesh, steps = 12): Rect {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i <= steps; i += 1) {
    for (let j = 0; j <= steps; j += 1) {
      const point = warpPoint(mesh, j / steps, i / steps);
      if (point.x < minX) minX = point.x;
      if (point.x > maxX) maxX = point.x;
      if (point.y < minY) minY = point.y;
      if (point.y > maxY) maxY = point.y;
    }
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}
